import { t, tf } from "./i18n.js";
// ---------------------------------------------------------------------------
// Heatmap der eigenen Wuerfe (KONZEPT_HEATMAP.md, Stufen P2 + P3)
//
// Datengrundlage ist die Supabase-Tabelle `dart_throws` (Stufe P1, Ingest in
// autoscore.js): jeder KAMERA-erkannte Dart mit Boardkoordinate in mm,
// Modus, Secure-Ziel, Konfidenz und Trainings-Flag. Handeingaben haben
// keine Koordinate und tauchen hier bewusst nicht auf (Konzept §6 -
// punktgenaue und feldgenaue Daten nicht mischen).
//
// Darstellung: die vorhandene SVG-Scheibe aus dartboard.js als Untergrund,
// darueber ein <canvas> gleicher Groesse. Jeder Wurf wird als Gauss-Kern
// gestempelt (Sigma 8 mm - in der Groessenordnung der gemessenen System-
// genauigkeit; schmaler waere Scheingenauigkeit), das Ergebnis auf das
// Maximum normalisiert und ueber EINE Farbrampe (Blau, dunkel -> hell,
// Deckkraft steigend) eingefaerbt. Eine einzelne Hue-Rampe statt eines
// Regenbogens: Auf der rot/gruenen Scheibe bleibt Blau als "Daten-Farbe"
// eindeutig, und Menge wird ueber Helligkeit gelesen, nicht ueber Farbton.
//
// Filter (P3): Zeitraum, Spielmodus, Training/Match, bei Secure die
// Zielzahl. Alle Filter sind reine Array-Filter auf den geladenen
// Rohdaten - nachgeladen wird nur, wenn der Zeitraum waechst.
//
// Datenschutz (Konzept §6): Die Heatmap gibt es NUR im eigenen Profil.
// Die RLS der Tabelle laesst ohnehin nur eigene Zeilen durch; fuer fremde
// Profile zeigt der Reiter einen Hinweis statt einer leeren Scheibe.
// ---------------------------------------------------------------------------

const HEAT_LIMIT = 5000; // juengste Wuerfe je Abfrage - stempelt in < 50 ms
// Kernbreite. UEBERARBEITET nach dem ersten Live-Blick (13.08. spaetabends):
// Mit 8 mm plus Wurzel-Kompression war das GANZE Board blau angehaucht -
// jeder Einzelwurf leuchtete halb so hell wie der dichteste Cluster, und
// echte Gruppen gingen im Schleier unter. Jetzt: engerer Kern (6 mm),
// LINEARE Dichte mit leichtem Gamma statt Wurzel, harter Unterschnitt
// fuer Restschleier - und die Einzelwuerfe kommen als eigene, scharfe
// Punktebene darueber (siehe stempleHeatmap).
const HEAT_SIGMA_MM = 6;
const HEAT_VIEW_MM = 210; // Halbe Kantenlaenge der SVG-Scheibe (dartboard.js)
const HEAT_CANVAS_PX = 480; // interne Aufloesung des Overlays
const HEAT_GAMMA = 0.75; // 1 = linear; < 1 hebt mittlere Dichten leicht an
const HEAT_SCHNITT = 0.07; // darunter: transparent (kein Grundschleier)

// Waermerampe mit MONOTON steigender Helligkeit (Inferno-artig): tiefes
// Indigo -> Magenta -> Orange -> helles Gelb. Kein Regenbogen (die
// Helligkeit ist die Ordnung), aber heisse Cluster springen als
// Orange/Gelb sofort ins Auge - genau die Beschwerde am Blau-Verlauf,
// bei dem "heiss" nur "etwas helleres Blau" war. Stops: [t, r, g, b, a].
const HEAT_RAMPE = [
  [0.0, 49, 46, 129, 0],
  [0.18, 76, 29, 149, 110],
  [0.4, 162, 28, 175, 165],
  [0.65, 234, 88, 12, 210],
  [1.0, 253, 224, 71, 242],
];

function heatFarbe(t) {
  for (let i = 1; i < HEAT_RAMPE.length; i++) {
    if (t <= HEAT_RAMPE[i][0]) {
      const [t0, r0, g0, b0, a0] = HEAT_RAMPE[i - 1];
      const [t1, r1, g1, b1, a1] = HEAT_RAMPE[i];
      const f = t1 > t0 ? (t - t0) / (t1 - t0) : 0;
      return [
        r0 + (r1 - r0) * f,
        g0 + (g1 - g0) * f,
        b0 + (b1 - b0) * f,
        a0 + (a1 - a0) * f,
      ];
    }
  }
  return HEAT_RAMPE[HEAT_RAMPE.length - 1].slice(1);
}

const heatZustand = {
  zeitraumTage: 90, // 0 = alles
  modus: "alle",
  training: "alle", // alle | training | match
  ziel: "alle", // nur Secure
  rohdaten: null, // geladene Zeilen des aktuellen Zeitraums
  geladenFuerTage: null,
  board: null,
  abgeschnitten: false, // HEAT_LIMIT erreicht -> Hinweis zeigen
};

function heatEl(id) {
  return document.getElementById(id);
}

// ── Laden ──────────────────────────────────────────────────────────────────

async function ladeHeatmapWuerfe() {
  if (
    heatZustand.rohdaten !== null &&
    heatZustand.geladenFuerTage === heatZustand.zeitraumTage
  ) {
    return heatZustand.rohdaten;
  }
  try {
    let query = window._supabase
      .from("dart_throws")
      .select("mode,target,base,mult,x_mm,y_mm,confidence,is_training,created_at")
      .eq("user_id", window.currentUser.id)
      .order("created_at", { ascending: false })
      .limit(HEAT_LIMIT);
    if (heatZustand.zeitraumTage > 0) {
      const ab = new Date(
        Date.now() - heatZustand.zeitraumTage * 24 * 3600 * 1000,
      );
      query = query.gte("created_at", ab.toISOString());
    }
    const { data, error } = await query;
    if (error) throw error;
    heatZustand.rohdaten = data || [];
    heatZustand.abgeschnitten = heatZustand.rohdaten.length >= HEAT_LIMIT;
    heatZustand.geladenFuerTage = heatZustand.zeitraumTage;
  } catch (err) {
    console.warn("Heatmap dart_throws:", err?.message || err);
    heatZustand.rohdaten = [];
    heatZustand.abgeschnitten = false;
    heatZustand.geladenFuerTage = heatZustand.zeitraumTage;
  }
  return heatZustand.rohdaten;
}

function gefilterteWuerfe() {
  const rows = heatZustand.rohdaten || [];
  return rows.filter((r) => {
    if (!Number.isFinite(r.x_mm) || !Number.isFinite(r.y_mm)) return false;
    if (heatZustand.modus !== "alle" && r.mode !== heatZustand.modus)
      return false;
    if (heatZustand.training === "training" && !r.is_training) return false;
    if (heatZustand.training === "match" && r.is_training) return false;
    if (
      heatZustand.modus === "secure" &&
      heatZustand.ziel !== "alle" &&
      String(r.target) !== heatZustand.ziel
    )
      return false;
    return true;
  });
}

// ── Zeichnen ───────────────────────────────────────────────────────────────

function stempleHeatmap(canvas, wuerfe) {
  const n = HEAT_CANVAS_PX;
  canvas.width = n;
  canvas.height = n;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, n, n);
  if (!wuerfe.length) return;

  const proMm = n / (2 * HEAT_VIEW_MM);
  const sigmaPx = HEAT_SIGMA_MM * proMm;
  const radius = Math.ceil(2.5 * sigmaPx);

  // Gauss-Kern einmal vorberechnen
  const seite = 2 * radius + 1;
  const kern = new Float32Array(seite * seite);
  for (let ky = -radius; ky <= radius; ky++) {
    for (let kx = -radius; kx <= radius; kx++) {
      kern[(ky + radius) * seite + (kx + radius)] = Math.exp(
        -(kx * kx + ky * ky) / (2 * sigmaPx * sigmaPx),
      );
    }
  }

  // Dichtefeld akkumulieren
  const feld = new Float32Array(n * n);
  for (const w of wuerfe) {
    const cx = Math.round((w.x_mm + HEAT_VIEW_MM) * proMm);
    const cy = Math.round((HEAT_VIEW_MM - w.y_mm) * proMm);
    for (let ky = -radius; ky <= radius; ky++) {
      const y = cy + ky;
      if (y < 0 || y >= n) continue;
      for (let kx = -radius; kx <= radius; kx++) {
        const x = cx + kx;
        if (x < 0 || x >= n) continue;
        feld[y * n + x] += kern[(ky + radius) * seite + (kx + radius)];
      }
    }
  }

  let max = 0;
  for (let i = 0; i < feld.length; i++) if (feld[i] > max) max = feld[i];
  if (max <= 0) return;

  // Einfaerben. LINEAR normalisiert (leichtes Gamma) statt Wurzel: Die
  // Wurzel hob jeden Einzelwurf auf halbe Cluster-Helligkeit - daher der
  // Blauschleier ueber dem ganzen Board. Jetzt traegt die Waerme-Ebene
  // nur noch DICHTE (Gruppen); unter HEAT_SCHNITT bleibt sie leer.
  // Einzelwuerfe gehen dadurch nicht verloren: Sie bekommen unten eine
  // eigene, scharfe Punktebene.
  const bild = ctx.createImageData(n, n);
  const px = bild.data;
  for (let i = 0; i < feld.length; i++) {
    const dichte = Math.pow(feld[i] / max, HEAT_GAMMA);
    if (dichte < HEAT_SCHNITT) continue;
    const [r, g, b, alpha] = heatFarbe(dichte);
    const j = i * 4;
    px[j] = r;
    px[j + 1] = g;
    px[j + 2] = b;
    px[j + 3] = alpha;
  }
  ctx.putImageData(bild, 0, 0);

  // ── Punktebene: jeder Wurf als kleiner, scharfer Punkt ──────────────
  // Damit "sticht" der einzelne Dart heraus (Wunsch 13.08. spaetabends),
  // waehrend die Waermeflaeche darunter die Gruppen zeigt. Weiss mit
  // dunklem Kern liest sich auf hellen UND dunklen Feldern.
  for (const w of wuerfe) {
    const x = (w.x_mm + HEAT_VIEW_MM) * proMm;
    const y = (HEAT_VIEW_MM - w.y_mm) * proMm;
    ctx.beginPath();
    ctx.arc(x, y, 2.0, 0, 2 * Math.PI);
    ctx.fillStyle = "rgba(255,255,255,0.7)";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x, y, 0.9, 0, 2 * Math.PI);
    ctx.fillStyle = "rgba(15,23,42,0.85)";
    ctx.fill();
  }
}

// ── Kennzahlen & Top-Felder ────────────────────────────────────────────────

function heatLabel(w) {
  if (typeof window.scoreAt === "function") {
    return window.scoreAt(w.x_mm, w.y_mm).label;
  }
  // Rueckfall aus base/mult, falls dartboard.js nicht geladen ist
  if (!w.base) return "MISS";
  if (w.base === 25) return "BULL";
  if (w.base === 50) return "B-EYE";
  return (w.mult === 3 ? "T" : w.mult === 2 ? "D" : "") + w.base;
}

function heatKennzahlen(wuerfe) {
  const anzahl = wuerfe.length;
  if (!anzahl) return { anzahl: 0 };

  // Streuung um den Schwerpunkt: mittlerer Abstand in mm
  const sx = wuerfe.reduce((a, w) => a + w.x_mm, 0) / anzahl;
  const sy = wuerfe.reduce((a, w) => a + w.y_mm, 0) / anzahl;
  const streuung =
    wuerfe.reduce((a, w) => a + Math.hypot(w.x_mm - sx, w.y_mm - sy), 0) /
    anzahl;

  const felder = new Map();
  for (const w of wuerfe) {
    const label = heatLabel(w);
    felder.set(label, (felder.get(label) || 0) + 1);
  }
  const top = [...felder.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);

  // Secure mit Zielfilter: Trefferquote ins anvisierte Feld
  let zielquote = null;
  if (heatZustand.modus === "secure" && heatZustand.ziel !== "alle") {
    const ziel = Number(heatZustand.ziel);
    const treffer = wuerfe.filter((w) => Number(w.base) === ziel).length;
    zielquote = Math.round((treffer / anzahl) * 100);
  }
  return { anzahl, streuung, top, zielquote };
}

// ── Aufbau des Reiters ─────────────────────────────────────────────────────

function heatFilterHtml(rows) {
  const modi = [...new Set(rows.map((r) => r.mode).filter(Boolean))].sort();
  const ziele = [
    ...new Set(
      rows
        .filter((r) => r.mode === "secure" && r.target != null)
        .map((r) => String(r.target)),
    ),
  ].sort((a, b) => Number(a) - Number(b));

  const opt = (wert, text, aktiv) =>
    `<option value="${wert}"${aktiv ? " selected" : ""}>${text}</option>`;

  let html = `<div class="heatmap-filter">`;
  html += `<select id="heatmap-zeitraum" class="heatmap-select">`;
  for (const [tage, text] of [
    [7, t("js.heatmap.008")],
    [30, t("js.heatmap.009")],
    [90, t("js.heatmap.010")],
    [0, t("js.heatmap.011")],
  ]) {
    html += opt(tage, text, heatZustand.zeitraumTage === tage);
  }
  html += `</select>`;

  html += `<select id="heatmap-modus" class="heatmap-select">`;
  html += opt("alle", t("js.heatmap.003"), heatZustand.modus === "alle");
  for (const m of modi) html += opt(m, m, heatZustand.modus === m);
  html += `</select>`;

  html += `<select id="heatmap-training" class="heatmap-select">`;
  html += opt("alle", t("js.heatmap.012"), heatZustand.training === "alle");
  html += opt("training", t("js.heatmap.013"), heatZustand.training === "training");
  html += opt("match", t("js.heatmap.014"), heatZustand.training === "match");
  html += `</select>`;

  if (heatZustand.modus === "secure" && ziele.length) {
    html += `<select id="heatmap-ziel" class="heatmap-select">`;
    html += opt("alle", t("js.heatmap.004"), heatZustand.ziel === "alle");
    for (const z of ziele) {
      html += opt(z, t("js.heatmap.005") + z, heatZustand.ziel === z);
    }
    html += `</select>`;
  }
  html += `</div>`;
  return html;
}

async function renderHeatmapTab(body) {
  // Fremdes Profil: bewusst keine Heatmap (Konzept §6, Datenschutz).
  if (window.profileViewIstFremd?.()) {
    body.innerHTML = `<div class="profile-empty">Die Wurf-Heatmap gibt es
      nur im eigenen Profil.</div>`;
    return;
  }

  body.innerHTML = `<div class="profile-empty">${t("js.heatmap.006")}</div>`;
  const rows = await ladeHeatmapWuerfe();

  if (!rows.length) {
    body.innerHTML = `<div class="profile-empty">Noch keine getrackten
      Würfe${heatZustand.zeitraumTage ? t("js.heatmap.001") : ""}.
      Die Heatmap füllt sich, sobald der Kamera-Tracker Würfe meldet
      (Handeingaben haben keine Position).</div>
      ${heatZustand.zeitraumTage ? heatFilterHtml([]) : ""}`;
    verdrahteHeatmapFilter(body);
    return;
  }

  const wuerfe = gefilterteWuerfe();
  const k = heatKennzahlen(wuerfe);

  body.innerHTML = `
    ${heatFilterHtml(rows)}
    <div class="heatmap-stage">
      <div id="heatmap-board"></div>
      <canvas id="heatmap-canvas"></canvas>
      <div id="heatmap-tooltip" class="heatmap-tooltip" style="display:none"></div>
    </div>
    <div class="profile-kpi-grid heatmap-kpis">
      <div class="profile-kpi"><div class="profile-kpi-value">${k.anzahl}</div>
        <div class="profile-kpi-label">Würfe (gefiltert)</div></div>
      <div class="profile-kpi"><div class="profile-kpi-value">${
        k.anzahl ? k.top[0][0] : "–"
      }</div><div class="profile-kpi-label">Häufigstes Feld</div></div>
      <div class="profile-kpi"><div class="profile-kpi-value">${
        k.anzahl ? k.streuung.toFixed(0) + " mm" : "–"
      }</div><div class="profile-kpi-label">Mittlere Streuung</div></div>
      ${
        k.zielquote !== null
          ? `<div class="profile-kpi"><div class="profile-kpi-value">${k.zielquote} %</div>
             <div class="profile-kpi-label">Treffer ins Ziel</div></div>`
          : ""
      }
    </div>
    ${
      k.anzahl
        ? `<div class="heatmap-top glass-panel"><b>Top-Felder:</b> ${k.top
            .map(([feld, zahl]) => `${feld} (${zahl})`)
            .join(" · ")}</div>`
        : `<div class="profile-empty">${t("js.heatmap.007")}</div>`
    }
    ${
      heatZustand.abgeschnitten
        ? `<div class="heatmap-hinweis">Anzeige auf die jüngsten ${HEAT_LIMIT}
           Würfe begrenzt – Zeitraum enger wählen für das vollständige Bild.</div>`
        : ""
    }`;

  // Scheibe zeichnen (nicht klickbar) und Canvas darueberlegen
  const boardWrap = heatEl("heatmap-board");
  if (boardWrap && typeof window.createDartboard === "function") {
    heatZustand.board = window.createDartboard(boardWrap, {
      interactive: false,
    });
  }
  const canvas = heatEl("heatmap-canvas");
  if (canvas) {
    stempleHeatmap(canvas, wuerfe);
    verdrahteHeatmapTooltip(canvas, wuerfe);
  }
  verdrahteHeatmapFilter(body);
}

function verdrahteHeatmapFilter(body) {
  const neuZeichnen = () => {
    const b = document.getElementById("profile-tab-body");
    if (b) renderHeatmapTab(b);
  };
  body.querySelector("#heatmap-zeitraum")?.addEventListener("change", (e) => {
    heatZustand.zeitraumTage = Number(e.target.value);
    neuZeichnen();
  });
  body.querySelector("#heatmap-modus")?.addEventListener("change", (e) => {
    heatZustand.modus = e.target.value;
    heatZustand.ziel = "alle"; // Zielfilter gehoert zu Secure
    neuZeichnen();
  });
  body.querySelector("#heatmap-training")?.addEventListener("change", (e) => {
    heatZustand.training = e.target.value;
    neuZeichnen();
  });
  body.querySelector("#heatmap-ziel")?.addEventListener("change", (e) => {
    heatZustand.ziel = e.target.value;
    neuZeichnen();
  });
}

// Feld unter dem Zeiger samt Wurfzahl - die Heatmap allein sagt "wo",
// der Tooltip sagt "was und wie oft".
function verdrahteHeatmapTooltip(canvas, wuerfe) {
  const tooltip = heatEl("heatmap-tooltip");
  if (!tooltip || typeof window.scoreAt !== "function") return;

  const proFeld = new Map();
  for (const w of wuerfe) {
    const label = heatLabel(w);
    proFeld.set(label, (proFeld.get(label) || 0) + 1);
  }

  canvas.addEventListener("mousemove", (event) => {
    const box = canvas.getBoundingClientRect();
    const xMm =
      ((event.clientX - box.left) / box.width) * 2 * HEAT_VIEW_MM -
      HEAT_VIEW_MM;
    const yMm =
      HEAT_VIEW_MM -
      ((event.clientY - box.top) / box.height) * 2 * HEAT_VIEW_MM;
    const feld = window.scoreAt(xMm, yMm).label;
    const zahl = proFeld.get(feld) || 0;
    tooltip.textContent = tf(
      zahl === 1 ? "js.heatmap.wurf1" : "js.heatmap.wurfN",
      { p1: feld, p2: zahl },
    );
    tooltip.style.display = "block";
    tooltip.style.left = event.clientX - box.left + 14 + "px";
    tooltip.style.top = event.clientY - box.top - 10 + "px";
  });
  canvas.addEventListener("mouseleave", () => {
    tooltip.style.display = "none";
  });
}

/** Cache leeren - z. B. nach neuen Wuerfen oder Kontowechsel. */
function resetHeatmap() {
  heatZustand.rohdaten = null;
  heatZustand.geladenFuerTage = null;
}

// ---------------------------------------------------------------
// Bruecke zu window: profile.js ruft renderHeatmapTab im Reiter auf.
// ---------------------------------------------------------------
Object.assign(window, { renderHeatmapTab, resetHeatmap });

export { renderHeatmapTab, resetHeatmap };
